import React, { useReducer, useContext, type ReactNode } from 'react';
import axios from 'axios';

import { AuthState } from 'services/auth';
import { API_URL } from 'consts';
import type { ReportSortMode, SetGoRequest, UpsertUserRequest, UserListItem } from 'types/api';

interface UserStateShape {
  loading: boolean;
  error: unknown;
  users: UserListItem[] | null;
}

interface UserContextValue extends UserStateShape {
  fetchUsers: (sortMode?: ReportSortMode) => Promise<void>;
  upsertUser: (user: UpsertUserRequest) => Promise<void>;
  deleteUser: (id: string) => Promise<void>;
  setGo: (payload: SetGoRequest) => Promise<void>;
  setReportSortOrder: (
    updates: Array<{ id: string; reportSortOrder: number | null }>,
  ) => Promise<void>;
}

type UserAction =
  | { type: 'FETCH_USERS_START' }
  | { type: 'SET_GO_START' }
  | { type: 'SET_GO_SUCCESS' }
  | { type: 'FETCH_USERS_SUCCESS'; users: UserListItem[] }
  | { type: 'FETCH_USERS_FAIL'; error: unknown }
  | { type: 'SET_GO_FAIL'; error: unknown }
  | { type: 'UPSERT_USER_START' }
  | { type: 'UPSERT_USER_SUCCESS' }
  | { type: 'UPSERT_USER_FAIL'; error: unknown }
  | { type: 'DELETE_USER_START' }
  | { type: 'DELETE_USER_SUCCESS' }
  | { type: 'DELETE_USER_FAIL'; error: unknown };

const initialState: UserStateShape = {
  loading: false,
  error: null,
  users: null,
};

const noopAsync = async () => {};

export const UserState = React.createContext<UserContextValue>({
  ...initialState,
  fetchUsers: noopAsync,
  upsertUser: noopAsync,
  deleteUser: noopAsync,
  setGo: noopAsync,
  setReportSortOrder: noopAsync,
});

const reducer = (state: UserStateShape, action: UserAction): UserStateShape => {
  switch (action.type) {
    case 'FETCH_USERS_START':
    case 'SET_GO_START':
    case 'UPSERT_USER_START':
    case 'DELETE_USER_START':
      return { ...state, loading: true, error: null };
    case 'SET_GO_SUCCESS':
    case 'UPSERT_USER_SUCCESS':
    case 'DELETE_USER_SUCCESS':
      return { ...state, loading: false };
    case 'FETCH_USERS_SUCCESS':
      return { ...state, loading: false, users: action.users };
    case 'FETCH_USERS_FAIL':
    case 'SET_GO_FAIL':
    case 'UPSERT_USER_FAIL':
    case 'DELETE_USER_FAIL':
      return { ...state, loading: false, error: action.error };
    default:
      return state;
  }
};

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const { access_token } = useContext(AuthState);
  const [state, dispatch] = useReducer(reducer, initialState);

  const fetchUsers = async (sortMode: ReportSortMode = 'name') => {
    dispatch({ type: 'FETCH_USERS_START' });

    try {
      const res = await axios.get<UserListItem[]>(`${API_URL}/users?sortMode=${sortMode}`, {
        headers: {
          authorization: `Bearer: ${access_token}`,
        },
      });

      dispatch({ type: 'FETCH_USERS_SUCCESS', users: res.data });
    } catch (error) {
      dispatch({ type: 'FETCH_USERS_FAIL', error });
    }
  };

  const setReportSortOrder = async (
    updates: Array<{ id: string; reportSortOrder: number | null }>,
  ) => {
    dispatch({ type: 'UPSERT_USER_START' });

    try {
      await axios({
        method: 'post',
        url: `${API_URL}/user/report-sort`,
        data: { updates },
        headers: {
          authorization: `Bearer: ${access_token}`,
        },
      });

      dispatch({ type: 'UPSERT_USER_SUCCESS' });
    } catch (error) {
      dispatch({ type: 'UPSERT_USER_FAIL', error });
    }
  };

  const upsertUser = async (user: UpsertUserRequest) => {
    dispatch({ type: 'UPSERT_USER_START' });

    try {
      await axios({
        method: 'post',
        url: `${API_URL}/user`,
        data: user,
        headers: {
          authorization: `Bearer: ${access_token}`,
        },
      });

      dispatch({ type: 'UPSERT_USER_SUCCESS' });
    } catch (error) {
      dispatch({ type: 'UPSERT_USER_FAIL', error });
    }
  };

  const deleteUser = async (id: string) => {
    dispatch({ type: 'DELETE_USER_START' });

    try {
      await axios({
        method: 'delete',
        url: `${API_URL}/user`,
        data: { id },
        headers: {
          authorization: `Bearer: ${access_token}`,
        },
      });

      dispatch({ type: 'DELETE_USER_SUCCESS' });
    } catch (error) {
      dispatch({ type: 'DELETE_USER_FAIL', error });
    }
  };

  const setGo = async ({ userId, year, kolicina }: SetGoRequest) => {
    dispatch({ type: 'SET_GO_START' });

    try {
      await axios({
        method: 'post',
        url: `${API_URL}/user/go`,
        data: { userId, godina: year, kolicina },
        headers: {
          authorization: `Bearer: ${access_token}`,
        },
      });

      dispatch({ type: 'SET_GO_SUCCESS' });
      await fetchUsers();
    } catch (error) {
      dispatch({ type: 'SET_GO_FAIL', error });
    }
  };

  const value: UserContextValue = {
    ...state,
    fetchUsers,
    upsertUser,
    deleteUser,
    setGo,
    setReportSortOrder,
  };

  return <UserState.Provider value={value}>{children}</UserState.Provider>;
};
